// node_modules
import {
    FC, useCallback, useContext, useEffect
} from "react";
// Types
import { TNotificationMessageDTO, TTechnologyHighlightDTO, TTechnologyHighlightsGroupDTO, TUpdateTechnologyHighlightsGroupsDTO } from "Types";
// Components
import { FindestButton, MarkdownItComponent } from "Components";
import { EditableTechnologyHighlight } from "../EditableTechnologyHighlight/EditableTechnologyHighlight";
// Styles
import styles from "./highlightsContainer.module.scss";
// Controllers
import { TechnologyHighlightsControllerSingleton } from "Controllers";
// Constants
import { HubConstants, TechnologyHighlightsConstants } from "Constants";
// Contexts
import { CaseContext, AuthContext } from "Stores";
import { WebsocketContext } from "Providers";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Enums
import { ToastTypeEnum } from "Enums";

type THighlightsContainer = {
    technologyHighlightsGroup: TTechnologyHighlightsGroupDTO,
    allTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO[],
    caseId: number,
    technologyId: number,
    isEditable: boolean,
    refreshTechnologyHighlightsGroups?: ((updateTechnologyHighlightsGroups: TUpdateTechnologyHighlightsGroupsDTO) => Promise<void>) | undefined,
    setNewSelectedGroupId?: ((newSelectedGroupId: number) => void) | undefined
}

export const HighlightsContainer: FC<THighlightsContainer> = ({ technologyHighlightsGroup, allTechnologyHighlightsGroup, caseId, technologyId, isEditable, refreshTechnologyHighlightsGroups, setNewSelectedGroupId }: THighlightsContainer) => {
    // Contexts
    const { caseState } = useContext(CaseContext);
    const { webSocketController } = useContext(WebsocketContext);
    const { authState } = useContext(AuthContext);

    // Let
    const highlightsGroupResourceName = `${HubConstants.HIGHLIGHTS_GROUP_RESOURCE_NAME_PREFIX}${technologyHighlightsGroup.id}${technologyId}`;

    const handleNewHighlightOnClick = async (): Promise<void> => {
        if (isEditable) {
            await TechnologyHighlightsControllerSingleton
                .createTechnologyHighlightAsync(caseId, technologyId, technologyHighlightsGroup.id, "", true);
        }
    };

    const getTechnologyHighlightsToDisplay = (): TTechnologyHighlightDTO[] => {
        if (technologyHighlightsGroup.id === TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID ||
            technologyHighlightsGroup.name.toLowerCase() === TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase()) {
            const allTechnologyHighlight: TTechnologyHighlightDTO[] = [];
            const highlightsAlreadyAdded = new Set<number>();

            for (const highlight of allTechnologyHighlightsGroup.map(group => group.highlights).flat()) {
                if (!highlightsAlreadyAdded.has(highlight.id)) {
                    allTechnologyHighlight.push(highlight);
                    highlightsAlreadyAdded.add(highlight.id);
                }
            }

            return allTechnologyHighlight;
        } else {
            return technologyHighlightsGroup.highlights;
        }
    };

    const handleHighlightsGroupAlreadyBeingEdited = useCallback((): void => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this group.");
    }, []);

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        if (!notificationMessage ||
            (notificationMessage && 
                (!notificationMessage.exclusionList || 
                !notificationMessage.message))) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, ToastHelperSingleton.defaultErrorMessage);
        }

        if (authState && authState.userEmail && !notificationMessage.exclusionList.includes(authState.userEmail.trim().toLowerCase())) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Notification, notificationMessage.message);
        }
    }, [authState]);

    useEffect(() => {
        if (!highlightsGroupResourceName || !refreshTechnologyHighlightsGroups) return;
        
        webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${highlightsGroupResourceName}`, 
            handleHighlightsGroupAlreadyBeingEdited);
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${highlightsGroupResourceName}`, 
            refreshTechnologyHighlightsGroups);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${highlightsGroupResourceName}`, 
            handleNotificationMessages);

        return () => {
            webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${highlightsGroupResourceName}`, 
                handleHighlightsGroupAlreadyBeingEdited);
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${highlightsGroupResourceName}`, 
                refreshTechnologyHighlightsGroups);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${highlightsGroupResourceName}`, 
                handleNotificationMessages);
        };
    }, [handleHighlightsGroupAlreadyBeingEdited, handleNotificationMessages, highlightsGroupResourceName, refreshTechnologyHighlightsGroups, webSocketController]);

    if (isEditable && setNewSelectedGroupId) {
        return (
            <div className={[styles.technologyHighlightsContentContainer, styles.editableHighlights].join(" ")}>
                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                    <div className={styles.technologyHighlightAddNewContainer}>
                        <FindestButton title={"+ CREATE NEW HIGHLIGHT"} onClick={handleNewHighlightOnClick} isColorless={true} isSmallSize={true} />
                    </div>
                    :
                    null
                }
                {
                    getTechnologyHighlightsToDisplay()
                        .map((highlight, index) => {
                            return (
                                <div className={styles.technologyEditableHighlightContainer} key={highlight.id}>
                                    <EditableTechnologyHighlight
                                        setNewSelectedGroupId={setNewSelectedGroupId}
                                        technologyHighlightsGroup={technologyHighlightsGroup}
                                        highlight={highlight}
                                        numbering={index + 1}
                                        allTechnologyHighlightsGroup={allTechnologyHighlightsGroup}
                                        technologyId={technologyId}
                                        highlightsGroupResourceName={highlightsGroupResourceName}
                                        highlightDescriptionResourceName={`${HubConstants.HIGHLIGHT_RESOURCE_NAME_PREFIX}Description${highlight.id}`}
                                        caseId={caseId} />
                                </div>
                            );
                        })
                }
            </div>
        );
    } else {
        return (
            <div className={styles.technologyHighlightsContentContainer}>
                {
                    technologyHighlightsGroup.highlights
                        .map((highlight) => {
                            return (
                            <div className={styles.technologyHighlight} key={highlight.id}>
                                <MarkdownItComponent 
                                    source={highlight.description}
                                    env={technologyId ? { "technologyId": technologyId } : undefined} />
                            </div>
                            );
                        })
                }
            </div>
        );
    }
};
